import { useCallback } from 'react';

import { useTranslate } from '@/i18n/use-locale';

/**
 * Number formatting for the admin console.
 *
 * ADR-013 fixes Western Arabic numerals (`0-9`) in data tables even when the interface
 * language is Arabic, because the tables are scanned as columns and Arabic-Indic digits
 * do not align under `tabular-nums`. Forcing the `en-US` numbering system is the whole
 * reason this is a helper rather than a bare `toLocaleString()` — the default would
 * follow the locale and quietly switch to `١٢٣` in Arabic.
 */
export function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * A file size, human-readable — `204800` becomes `200 KB`, `2202009` becomes `2.1 MB`.
 *
 * The units (`KB`/`MB`) are locale-agnostic SI-ish abbreviations rather than translatable
 * copy, and the digits stay Latin for the same reason `formatCount` forces them (ADR-013):
 * the audit table is scanned as a column. Bytes below a kilobyte are shown whole; kilobytes
 * are rounded; megabytes keep one decimal, which is what the design's `2.1 MB` expects.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${formatCount(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${formatCount(Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * A running time in whole seconds as `m:ss` — `42` becomes `0:42`, `75` becomes `1:15`.
 *
 * The clock form every video player shows, so it needs no words in either language, and
 * its digits stay Latin for the reason `formatCount` gives.
 */
export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * Riyals, in the admin's language — `190 ر.س` in Arabic, `SAR 190` in English.
 *
 * A template rather than a suffix, because the unit sits on a different side of the
 * number in each language. The digits stay Latin for the reason `formatCount` gives.
 */
export function useFormatSar(): (amount: number) => string {
  const t = useTranslate();
  return useCallback((amount) => t('common.currency.amount', { amount: formatCount(amount) }), [t]);
}
